/**
 * PermissionRoute — route guard using RBAC permissions (nopCommerce ACL pattern).
 *
 * Permissions are the single source of truth. No role fallbacks.
 * While permissions load, shows a spinner — never denies prematurely.
 *
 * Usage:
 *   <PermissionRoute permission="agents:read">
 *     <AgentManagement />
 *   </PermissionRoute>
 */

import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { usePermissions } from './PermissionContext';
import RedirectToLogin from './RedirectToLogin';


const Spinner = () => (
    <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
    </div>
);

export default function PermissionRoute({ permission, anyOf, children }) {
    const { isAuthenticated, isLoading: authLoading } = useAuth();
    const { hasPermission, hasAnyPermission, permissionsLoaded, permissionsFailed, refreshPermissions } = usePermissions();

    // Still loading auth
    if (authLoading) return <Spinner />;

    // Not authenticated — preserve the intended URL for post-login return
    if (!isAuthenticated) return <RedirectToLogin />;

    // Permissions not loaded yet — wait (never deny prematurely)
    if (!permissionsLoaded) return <Spinner />;

    // Permission fetch failed — show error with retry
    if (permissionsFailed) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen text-center p-4">
                <p className="text-gray-600 mb-4">Unable to verify permissions. Please try again.</p>
                <button
                    onClick={refreshPermissions}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                    Retry
                </button>
            </div>
        );
    }

    // Permission check (single source of truth)
    let allowed = false;
    if (permission) {
        allowed = hasPermission(permission);
    } else if (anyOf) {
        allowed = hasAnyPermission(...anyOf);
    }

    return allowed ? children : <Navigate to="/access-denied" replace />;
}
